import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import Cookies from 'js-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCreditCard, faShoppingCart, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import '../../styles/UserCenter.css';
import { useTranslation } from 'react-i18next';

// 内联占位图，避免外部依赖
const PLACEHOLDER_IMG = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="150"><rect width="100%" height="100%" fill="#eeeeee"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999999" font-size="16">No Image</text></svg>';

const isBrowser = typeof window !== 'undefined';
const isPages = isBrowser && /pages\.dev$/.test(window.location.hostname);
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || (isPages ? 'https://api-do.whitelotus.work' : ''));

// 礼品卡数据
const giftCardOptions = [
  {
    id: 1,
    name: '健身初体验卡',
    value: 200,
    price: 180,
    description: '适合初次体验健身服务的新用户，包含5次入场券和2次教练指导服务',
    image: PLACEHOLDER_IMG,
    validPeriod: '6个月'
  },
  {
    id: 2,
    name: '全能健身卡',
    value: 500,
    price: 430,
    description: '全场通用健身卡，可用于任何健身项目及部分教练预约服务',
    image: PLACEHOLDER_IMG,
    validPeriod: '12个月'
  },
  {
    id: 3,
    name: '豪华健身礼包',
    value: 1000,
    price: 800,
    description: '顶级会员健身卡，包含所有健身项目、教练预约及专属VIP服务',
    image: PLACEHOLDER_IMG,
    validPeriod: '24个月'
  },
  {
    id: 4,
    name: '定制健身卡',
    value: 300,
    price: 250,
    description: '可根据个人需求定制健身服务的礼品卡，灵活选择健身项目',
    image: PLACEHOLDER_IMG,
    validPeriod: '12个月'
  }
];

const GiftCards = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('balance');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 从localStorage获取用户信息
    let storedUser = localStorage.getItem('userData');
    
    // 如果userData不存在，尝试使用user键
    if (!storedUser) {
      storedUser = localStorage.getItem('user');
      // 如果找到了user信息，同时存储到userData中以保持一致性
      if (storedUser) {
        localStorage.setItem('userData', storedUser);
      }
    }
    
    if (!storedUser) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
      
      // 确保money字段存在
      if (parsedUser.yue && !parsedUser.money) {
        parsedUser.money = parsedUser.yue;
        localStorage.setItem('userData', JSON.stringify(parsedUser));
      }
    } catch (err) {
      console.error('Error parsing user data', err);
      navigate('/login');
    }
  }, [navigate]);

  const handleSelectCard = (card) => {
    setSelectedCard(card);
    setShowCheckout(true);
    // 页面滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const handleGoBack = () => {
    if (showCheckout) {
      setShowCheckout(false);
      setSelectedCard(null);
    } else {
      // 返回到用户详情页而不是用户中心
      if (userData && userData.id) {
        navigate(`/users/${userData.id}`);
      } else {
        navigate('/user-center');
      }
    }
  };

  const handlePurchase = async () => {
    if (!selectedCard || !userData) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // 检查余额是否足够（如果使用余额支付）
      if (paymentMethod === 'balance' && userData.money < selectedCard.price) {
        setError(t('payment.insufficientBalance'));
        setLoading(false);
        return;
      }
      
      // 调用购买礼品卡API
      const response = await axios.post(`/giftCard/purchase`, {
        userId: userData.id,
        giftCardId: selectedCard.id,
        price: selectedCard.price,
        paymentMethod: paymentMethod
      }, {
        headers: {
          'token': Cookies.get('token')
        }
      });
      
      if (response.data.code === 0) {
        setSuccess(true);
        
        // 如果使用余额支付，更新本地存储的用户余额
        if (paymentMethod === 'balance') {
          const updatedUserData = {
            ...userData,
            money: userData.money - selectedCard.price
          };
          localStorage.setItem('userData', JSON.stringify(updatedUserData));
          localStorage.setItem('user', JSON.stringify(updatedUserData)); // 同时更新user键
          setUserData(updatedUserData);
        }
        
        // 3秒后返回用户详情页
        setTimeout(() => {
          navigate(`/users/${userData.id}`);
        }, 3000);
      } else {
        setError(response.data.msg || t('giftCards.purchaseFailed'));
      }
    } catch (err) {
      console.error('Error purchasing gift card', err);
      setError(t('payment.networkError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="user-center-container">
        <div className="success-message">
          <FontAwesomeIcon icon={faCheckCircle} size="3x" />
          <h2>{t('giftCards.purchaseSuccess')}</h2>
          <p>{t('giftCards.purchaseSuccessDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-center-container">
      <div className="page-header">
        <button className="btn-link" onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> 
          {showCheckout ? t('giftCards.backToCards') : t('giftCards.backToCenter')}
        </button>
        <h1>{showCheckout ? t('giftCards.confirmPurchase') : t('giftCards.title')}</h1>
      </div>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationCircle} /> {error}
        </div>
      )}

      {showCheckout && selectedCard ? (
        <div className="gift-card-checkout">
          <div className="gift-card-checkout-info">
            <div className="gift-card-checkout-image">
              <img 
                src={selectedCard.image || PLACEHOLDER_IMG} 
                alt={selectedCard.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = PLACEHOLDER_IMG;
                }}
              />
            </div>
            <div className="gift-card-checkout-details">
              <h2>{selectedCard.name}</h2>
              <p className="gift-card-value">{t('payment.paymentAmount')}: ¥{selectedCard.value}</p>
              <p className="gift-card-price">{t('home.price')}: <span className="discount-price">¥{selectedCard.price}</span></p>
              <p className="gift-card-valid">{t('recharge.loading')}: {selectedCard.validPeriod}</p>
              <p className="gift-card-description">{selectedCard.description}</p>
            </div>
          </div>

          <div className="payment-section">
            <h3>{t('payment.selectPaymentMethod')}</h3>
            <div className="payment-methods">
              <div 
                className={`payment-method ${paymentMethod === 'balance' ? 'active' : ''}`}
                onClick={() => handlePaymentMethodChange('balance')}
              >
                <div className="payment-method-icon">
                  <FontAwesomeIcon icon={faCreditCard} />
                </div>
                <div className="payment-method-info">
                  <div className="payment-method-name">{t('payment.memberBalance')}</div>
                  <div className="payment-method-balance">{t('payment.paymentAmount')}: ¥{userData?.money || 0}</div>
                </div>
              </div>
              
              <div 
                className={`payment-method ${paymentMethod === 'wechat' ? 'active' : ''}`}
                onClick={() => handlePaymentMethodChange('wechat')}
              >
                <div className="payment-method-icon">
                  <img src="/assets/wechat-pay.png" alt="WeChat Pay" />
                </div>
                <div className="payment-method-name">{t('payment.wechat')}</div>
              </div>
              
              <div 
                className={`payment-method ${paymentMethod === 'alipay' ? 'active' : ''}`}
                onClick={() => handlePaymentMethodChange('alipay')}
              >
                <div className="payment-method-icon">
                  <img src="/assets/alipay.png" alt="Alipay" />
                </div>
                <div className="payment-method-name">{t('payment.alipay')}</div>
              </div>
            </div>

            <div className="checkout-total">
              <div className="checkout-total-label">{t('payment.paymentAmount')}:</div>
              <div className="checkout-total-price">¥{selectedCard.price}</div>
            </div>

            <button 
              className="checkout-btn"
              onClick={handlePurchase}
              disabled={loading}
            >
              {loading ? (
                <span>{t('payment.processing')}</span>
              ) : (
                <>
                  <FontAwesomeIcon icon={faShoppingCart} /> {t('giftCards.confirmPurchase')}
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="gift-cards-container">
          <p className="gift-cards-intro">{t('giftCards.title')}</p>
          
          <div className="gift-cards-grid">
            {giftCardOptions.map(card => (
              <div key={card.id} className="gift-card">
                <div className="gift-card-image">
                  <img 
                    src={card.image || PLACEHOLDER_IMG} 
                    alt={card.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = PLACEHOLDER_IMG;
                    }}
                  />
                </div>
                <div className="gift-card-content">
                  <h3>{card.name}</h3>
                  <div className="gift-card-info">
                    <div className="gift-card-value">{t('payment.paymentAmount')}: ¥{card.value}</div>
                    <div className="gift-card-price">{t('home.price')}: <span className="discount-price">¥{card.price}</span></div>
                  </div>
                  <p className="gift-card-description">{card.description}</p>
                  <div className="gift-card-footer">
                    <span className="gift-card-valid">{t('recharge.loading')}: {card.validPeriod}</span>
                    <button 
                      className="gift-card-btn"
                      onClick={() => handleSelectCard(card)}
                    >
                      {t('giftCards.confirmPurchase')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftCards; 